import { alpha, createTheme } from "@mui/material";
import { defaultThemePalette } from "../palettes/default.palette";

const fonts = "Inter, sans-serif";

export const customMuiTheme = createTheme(
    {
        breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                md: 960,
                lg: 1366,
                xl: 1920,
            },
        },
        components: {
            MuiTypography: {
                defaultProps: {
                    fontFamily: fonts,
                    lineHeight: "100%",
                },
                styleOverrides: {
                    h3: {
                        fontSize: "32px",
                        fontWeight: "700",
                        lineHeight: "48px",
                        color: defaultThemePalette.palette.text.primary,
                    },
                    h4: {
                        fontSize: "24px",
                        fontWeight: "700",
                        lineHeight: "36px",
                        color: defaultThemePalette.palette.text.primary,
                    },
                    h5: {
                        fontSize: "20px",
                        fontWeight: "700",
                        lineHeight: "30px",
                        color: defaultThemePalette.palette.text.primary,
                    },
                    h6: {
                        fontSize: "18px",
                        fontWeight: "700",
                        lineHeight: "24px",
                        color: defaultThemePalette.palette.text.primary,
                    },
                    body1: {
                        fontSize: "16px",
                        fontWeight: "400",
                        lineHeight: "24px",
                        color: defaultThemePalette.palette.text.primary,
                    },
                    body2: {
                        fontSize: "14px",
                        fontWeight: "400",
                        lineHeight: "22px",
                        color: defaultThemePalette.palette.text.secondary,
                    },
                    subtitle1: {
                        fontSize: "16px",
                        fontWeight: "600",
                        lineHeight: "23px",
                        color: defaultThemePalette.palette.text.primary,
                    },
                    subtitle2: {
                        fontSize: "14px",
                        fontWeight: "600",
                        lineHeight: "22px",
                        color: defaultThemePalette.palette.text.primary,
                    },
                    caption: {
                        fontSize: "12px",
                        fontWeight: "400",
                        lineHeight: "18px",
                        color: defaultThemePalette.palette.text.secondary,
                    },
                    overline: {
                        fontSize: "12px",
                        fontWeight: "700",
                        lineHeight: "18px",
                        color: defaultThemePalette.palette.text.secondary,
                        letterSpacing: "0",
                    },
                },
            },

            MuiInput: {
                styleOverrides: {
                    root: {
                        color: "gray",
                        "&::before": {
                            borderBottom: "2px solid #2CABF9",
                        },

                        "&:hover:not(.Mui-disabled):before": {
                            borderBottomColor:
                                defaultThemePalette.palette.secondary.main,
                        },

                        "&.Mui-focused:after": {
                            borderBottomColor:
                                defaultThemePalette.palette.secondary.main,
                        },
                        input: {
                            "&::placeholder": {
                                color: "#2CABF9", // sua cor aqui
                                opacity: 0.5, // garante que a cor apareça
                            },
                        },
                    },
                },
            },

            MuiOutlinedInput: {
                defaultProps: {
                    color: "secondary",
                },
                styleOverrides: {
                    root: {
                        fontFamily: fonts,
                        borderRadius: "8px",
                        paddingLeft: "0px",
                        "&:has([readonly])": {
                            color: defaultThemePalette.palette.text.disabled,
                        },
                    },
                    notchedOutline: {
                        borderColor:
                            defaultThemePalette.palette.outlineInput.main,
                    },
                    multiline: {
                        fontFamily: fonts,
                        paddingLeft: "15px",
                    },
                    inputMultiline: {
                        color: defaultThemePalette.palette.text.primary,
                        fontSize: "16px",
                    },
                },
            },

            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: defaultThemePalette.palette.text.disabled,
                        fontFamily: fonts,
                        "&.MuiInputLabel-root.Mui-focused": {
                            color: defaultThemePalette.palette.primary.main,
                        },
                    },
                },
            },
            MuiButton: {
                defaultProps: {
                    color: "primary",
                },
                styleOverrides: {
                    root: {
                        height: "55px",
                        boxShadow: "none",
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: "700",
                        fontSize: "15px",
                        lineHeight: "26px",
                        fontFamily: fonts,
                        minWidth: "fit-content",
                        "&.Mui-disabled": {
                            backgroundColor: "#B0B0B0",
                            color: "#7C7C7C",
                        },
                    },
                    sizeLarge: {
                        height: "48px",
                        fontSize: "15px",
                        lineHeight: "26px",
                    },
                    sizeMedium: {
                        height: "36px",
                        fontSize: "14px",
                        lineHeight: "24px",
                        padding: "6px 16px",
                    },
                    sizeSmall: {
                        height: "30px",
                        fontSize: "13px",
                        lineHeight: "22px",
                        padding: "4px 10px",
                    },
                    colorInherit: {
                        color: defaultThemePalette.palette.text.primary,
                        border: `solid 1px ${defaultThemePalette.palette.outlineInput.main}`,
                    },
                },
            },
            MuiLoadingButton: {
                styleOverrides: {
                    root: {
                        "&.MuiLoadingButton-loading": {
                            backgroundColor: alpha(
                                defaultThemePalette.palette.secondary.main,
                                0.08
                            ),
                        },
                        fontFamily: fonts,
                    },
                },
            },
            MuiBadge: {
                styleOverrides: {
                    badge: {
                        fontSize: "12px",
                        fontWeight: "700",
                        lineHeight: "20px",
                        fontFamily: fonts,
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor:
                            defaultThemePalette.palette.background.default,
                        color: defaultThemePalette.palette.text.primary,
                        fontSize: "12px",
                        fontWeight: "400",
                        textAlign: "center",
                        padding: "10px 12px",
                        boxShadow:
                            "-20px 20px 40px -4px rgba(145, 158, 171, 0.24)",
                        filter: "drop-shadow(0px 0px 2px rgba(145, 158, 171, 0.24))",
                        borderRadius: "8px",
                        fontFamily: fonts,
                    },
                    arrow: {
                        color: defaultThemePalette.palette.background.default,
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        fontFamily: fonts,
                        lineHeight: "15px",
                        backgroundColor: alpha(
                            defaultThemePalette.palette.lightContrast.main,
                            0.16
                        ),
                        color: defaultThemePalette.palette.text.secondary,
                        fontWeight: "400",
                        fontSize: "13px",
                    },
                },
            },
            MuiSkeleton: {
                defaultProps: {
                    animation: "wave",
                },
                styleOverrides: {
                    root: {
                        "&.MuiSkeleton-rounded": {
                            borderRadius: "16px",
                        },
                    },
                },
            },
            MuiDataGrid: {
                defaultProps: {
                    rowHeight: 76,
                    disableColumnMenu: true,
                },
                styleOverrides: {
                    root: {
                        border: "none",
                    },
                    cell: {
                        border: "none",
                        "&.MuiDataGrid-cell:focus": {
                            outline: "none",
                        },
                        "&.MuiDataGrid-cell:focus-within": {
                            outline: "none",
                        },
                    },
                    withBorderColor: {
                        outline: "none",
                    },
                    columnHeader: {
                        backgroundColor:
                            defaultThemePalette.palette.backgroundContrast.main,
                        fontFamily: fonts,
                    },
                    columnHeaderTitle: {
                        fontSize: "13px",
                        color: defaultThemePalette.palette.text.secondary,
                        fontWeight: "600",
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        fontFamily: fonts,
                    },
                },
            },
            MuiPopover: {
                styleOverrides: {
                    paper: {
                        boxShadow: `0px 0px 2px 0px #919EAB3D`,
                        borderRadius: "8px",
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: {
                        borderBottom: `solid 1px ${defaultThemePalette.palette.divider}`,
                    },
                    indicator: {
                        backgroundColor:
                            defaultThemePalette.palette.secondary.dark,
                    },
                    flexContainer: {
                        gap: "40px",
                        paddingLeft: "24px",
                    },
                },
            },
            MuiTab: {
                defaultProps: {
                    disableRipple: true,
                },
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        fontFamily: fonts,
                        fontWeight: "600",
                        "&.Mui-selected": {
                            color: defaultThemePalette.palette.text.primary,
                        },
                        padding: "0px",
                        minWidth: "0px",
                        lineHeight: "10px",
                        minHeight: "50px",
                    },
                },
            },
        },
    },
    defaultThemePalette
);
